import merge from 'lodash/merge';

export const PORTAL_UI_NAMESPACE = 'portal-ui';

type WidenStrings<T> = T extends string
  ? string
  : T extends object
    ? { [K in keyof T]: WidenStrings<T[K]> }
    : T;

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

type UnionToIntersection<T> = (
  T extends unknown ? (argument: T) => void : never
) extends (argument: infer I) => void
  ? I
  : never;

export type PortalUiResources<TCatalog> = {
  en: { [PORTAL_UI_NAMESPACE]: TCatalog };
  de: { [PORTAL_UI_NAMESPACE]: TCatalog };
  es: { [PORTAL_UI_NAMESPACE]: TCatalog };
};

type PortalUiResourceBundle = {
  en: { [PORTAL_UI_NAMESPACE]: object };
  de: { [PORTAL_UI_NAMESPACE]: object };
  es: { [PORTAL_UI_NAMESPACE]: object };
};

/**
 * Wraps one component catalog in the `portal-ui` namespace.
 * German and Spanish must contain the same keys as English.
 */
export function createPortalUiResources<const TCatalog>(catalogs: {
  en: TCatalog;
  de: NoInfer<WidenStrings<TCatalog>>;
  es: NoInfer<WidenStrings<TCatalog>>;
}): PortalUiResources<WidenStrings<TCatalog>> {
  return {
    en: { [PORTAL_UI_NAMESPACE]: catalogs.en as WidenStrings<TCatalog> },
    de: { [PORTAL_UI_NAMESPACE]: catalogs.de },
    es: { [PORTAL_UI_NAMESPACE]: catalogs.es },
  };
}

/**
 * Merges portal-ui catalogs into an app's i18next resources.
 * Later catalogs and `overrides` replace matching keys only.
 */
export function withPortalUiResources<
  TAppResources extends Record<string, object>,
  const TPortalResources extends readonly [
    PortalUiResourceBundle,
    ...PortalUiResourceBundle[],
  ],
>(
  appResources: TAppResources,
  portalResources: TPortalResources,
  overrides?: DeepPartial<UnionToIntersection<TPortalResources[number]>>
): UnionToIntersection<TPortalResources[number]> & TAppResources {
  const resources = {};

  for (const catalog of portalResources) {
    merge(resources, catalog);
  }

  return merge(resources, appResources, overrides) as UnionToIntersection<
    TPortalResources[number]
  > &
    TAppResources;
}
